import type { UseQueryOptions } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  PoktanDataType,
  PoktanInfiniteQueryOptions,
  PoktanResponseDataType
} from './types'

const POKTAN_QUERY_KEY = (params: {
  page: number
  per_page: number
  province_code?: string
  city_code?: string
  district_code?: string
  village_code?: string
  search: string
}) => ['reference_poktan', { ...params }] as const

type PoktanQueryKey = ReturnType<typeof POKTAN_QUERY_KEY>

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
    province_code: string
    city_code: string
    district_code: string
    village_code: string
  }>
): PoktanInfiniteQueryOptions<PoktanQueryKey> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 10,
    search: opts?.search ?? '',
    province_code: opts?.province_code,
    city_code: opts?.city_code,
    district_code: opts?.district_code,
    village_code: opts?.village_code
  }

  return {
    queryKey: POKTAN_QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async ({ pageParam }) => {
      try {
        const res = await api.get<PoktanResponseDataType>(
          '/report/reference/poktan',
          {
            params: {
              page: pageParam,
              per_page: params.per_page,
              search:
                params.search.length >= 2 ? `%${params.search}%` : undefined,
              province_code: params.province_code,
              city_code: params.city_code,
              district_code: params.district_code,
              village_code: params.village_code
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const POKTAN_BY_ID_QUERY_KEY = (params: { id: string }) =>
  ['reference_poktan', params] as const

const getById = (opts: {
  id: string
}): UseQueryOptions<ServiceGetByIdResponse<PoktanDataType>> => ({
  queryKey: POKTAN_BY_ID_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const res = await api.get<ServiceGetAllResponse<PoktanDataType[]>>(
        '/report/reference/poktan',
        {
          params: {
            id: opts.id
          }
        }
      )

      const data = res.data.data?.[0]
      if (!data) {
        throw new Error('Poktan not found')
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const poktanReference = {
  queryKey: {
    all: POKTAN_QUERY_KEY,
    byId: POKTAN_BY_ID_QUERY_KEY
  },
  batched: getBatch,
  byId: getById
}
